import React, { useState, useEffect, useRef } from 'react';
import Nango from '@nangohq/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useToast } from '../components/Toast';
import {
  useSettings, useProfiles, useApiKeys,
  useIntegrationProviders, useIntegrationConnections, useUsage, QK,
} from '../queries';

// ── Shared styles ─────────────────────────────────────────────────────────────

const s = {
  page: {
    padding: '40px 48px',
    maxWidth: 680,
    color: '#334155',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  heading: { fontSize: 22, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' },
  sub: { fontSize: 14, color: '#64748b', margin: '0 0 28px' },
  tabs: { display: 'flex', gap: 4, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 },
  tab: (active) => ({
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? '#0f172a' : '#64748b',
    background: 'none',
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    cursor: 'pointer',
    marginBottom: -1,
    transition: 'color 0.15s',
  }),
  sectionLabel: {
    fontSize: 12, fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12,
  },
  card: (selected, disabled) => ({
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '13px 16px', borderRadius: 8, marginBottom: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: `1px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
    background: selected ? '#eff6ff' : '#fff',
    opacity: disabled ? 0.55 : 1,
    transition: 'border-color 0.15s, background 0.15s',
  }),
  radioOuter: (selected) => ({
    width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
    border: `2px solid ${selected ? '#2563eb' : '#cbd5e1'}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }),
  radioInner: { width: 7, height: 7, borderRadius: '50%', background: '#2563eb' },
  optLabel: { fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 2 },
  optDesc: { fontSize: 13, color: '#64748b' },
  upgradeNote: {
    marginBottom: 20, padding: '12px 14px',
    background: '#fffbeb', border: '1px solid #fcd34d',
    borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.5,
  },
  saveBtn: (disabled) => ({
    marginTop: 24, padding: '10px 24px',
    background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 8, fontWeight: 600, fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
  }),
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderRadius: 8, marginBottom: 8,
    border: '1px solid #e2e8f0',
    background: '#fff',
  },
  rowLabel: { fontSize: 14, color: '#0f172a', fontWeight: 500 },
  rowSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
  dangerBtn: {
    padding: '6px 14px', background: '#fff', color: '#dc2626',
    border: '1px solid #fecaca', borderRadius: 6,
    fontSize: 13, cursor: 'pointer', fontWeight: 500,
  },
  smallBtn: {
    padding: '6px 14px', background: '#fff', color: '#374151',
    border: '1px solid #e2e8f0', borderRadius: 6,
    fontSize: 13, cursor: 'pointer', fontWeight: 500,
  },
  primarySmallBtn: {
    padding: '6px 14px', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500,
  },
  input: {
    width: '100%', padding: '9px 12px',
    background: '#fff', border: '1px solid #cbd5e1',
    borderRadius: 7, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
  },
  inputRow: { display: 'flex', gap: 8, marginBottom: 12 },
  emptyState: { fontSize: 14, color: '#94a3b8', padding: '20px 0', textAlign: 'center' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12,
    fontSize: 11, fontWeight: 600, background: color + '22', color,
  }),
  statGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 },
  statBox: {
    padding: '16px 20px', borderRadius: 10,
    border: '1px solid #e2e8f0',
    background: '#fff',
  },
  statLabel: { fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  statValue: { fontSize: 24, fontWeight: 700, color: '#0f172a' },
  statMax: { fontSize: 13, color: '#94a3b8', marginTop: 2 },
  keyBox: {
    fontFamily: 'monospace', fontSize: 13, background: '#f8fafc',
    border: '1px solid #e2e8f0', borderRadius: 6,
    padding: '10px 14px', color: '#16a34a', wordBreak: 'break-all', marginBottom: 12,
  },
  formGroup: { marginBottom: 14 },
  formLabel: { fontSize: 13, color: '#374151', fontWeight: 500, marginBottom: 5, display: 'block' },
  select: {
    width: '100%', padding: '9px 12px',
    background: '#fff', border: '1px solid #cbd5e1',
    borderRadius: 7, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
  },
};

// ── Frequency options ─────────────────────────────────────────────────────────

const FREQUENCIES = [
  { value: 'daily',   label: 'Daily summary', description: 'One email a day with the new matches since yesterday.' },
  { value: 'weekly',  label: 'Weekly summary', description: 'One email a week rounding up your new matches.' },
  { value: 'off',     label: 'Off', description: 'No emails — I\'ll check the dashboard myself.' },
];

// ── Tab: Email Notifications ──────────────────────────────────────────────────

// ISO 3166-1 alpha-3 — matches the `country` format stored on tenders (e.g. DEU, FRA).
const COUNTRIES = [
  ['AUT', 'Austria'], ['BEL', 'Belgium'], ['BGR', 'Bulgaria'], ['HRV', 'Croatia'],
  ['CYP', 'Cyprus'], ['CZE', 'Czechia'], ['DNK', 'Denmark'], ['EST', 'Estonia'],
  ['FIN', 'Finland'], ['FRA', 'France'], ['DEU', 'Germany'], ['GRC', 'Greece'],
  ['HUN', 'Hungary'], ['ISL', 'Iceland'], ['IRL', 'Ireland'], ['ITA', 'Italy'],
  ['LVA', 'Latvia'], ['LIE', 'Liechtenstein'], ['LTU', 'Lithuania'], ['LUX', 'Luxembourg'],
  ['MLT', 'Malta'], ['NLD', 'Netherlands'], ['NOR', 'Norway'], ['POL', 'Poland'],
  ['PRT', 'Portugal'], ['ROU', 'Romania'], ['SVK', 'Slovakia'], ['SVN', 'Slovenia'],
  ['ESP', 'Spain'], ['SWE', 'Sweden'], ['CHE', 'Switzerland'],
];

const hint = { fontWeight: 400, color: '#94a3b8', fontSize: 12 };
const checkBox = {
  display: 'flex', flexWrap: 'wrap', gap: '9px 16px',
  maxHeight: 142, overflowY: 'auto',
  padding: '11px 13px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff',
};
const clampScore = (v) => {
  const n = Math.round(Number(v));
  return Number.isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
};
const stepBtn = (off) => ({
  width: 40, flexShrink: 0, border: '1px solid #cbd5e1', background: '#fff',
  color: '#0f172a', borderRadius: 7, fontSize: 18, fontWeight: 600,
  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.45 : 1, lineHeight: 1,
});

function EmailNotificationsTab() {
  const [frequency, setFrequency] = useState('off');
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [notifEmail, setNotifEmail] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [minScore, setMinScore] = useState(65);
  const [profileIds, setProfileIds] = useState([]);
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const showToast = useToast();
  const queryClient = useQueryClient();
  const settingsQuery = useSettings();
  const profilesQuery = useProfiles();
  const profiles = (profilesQuery.data ?? []).map((p) => ({ id: p.id, profile_name: p.profile_name }));
  const loading = settingsQuery.isLoading;
  const seededRef = useRef(false);

  // Seed the editable fields once from /settings; a background refetch (e.g. on
  // tab refocus) must not clobber the user's in-progress edits.
  useEffect(() => {
    if (seededRef.current || settingsQuery.isLoading) return;
    const data = settingsQuery.data;
    if (data) {
      setFrequency(data.notification_frequency || 'off');
      setAlertsEnabled(!!data.email_alerts_enabled);
      setNotifEmail(data.notification_email || '');
      setAccountEmail(data.account_email || '');
      const f = data.notification_filters || {};
      setMinScore(f.min_score ?? 65);
      setProfileIds(f.profile_ids || []);
      setCountries(f.countries || []);
    }
    seededRef.current = true;
  }, [settingsQuery.isLoading, settingsQuery.data]);

  const toggle = (arr, val, setter) =>
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  async function handleSave() {
    setSaving(true);
    const notification_filters = {};
    if (Number(minScore) > 0) notification_filters.min_score = Number(minScore);
    if (profileIds.length) notification_filters.profile_ids = profileIds;
    if (countries.length) notification_filters.countries = countries;
    try {
      await apiFetch('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          notification_frequency: frequency,
          notification_filters,
          notification_email: notifEmail.trim(),
        }),
      });
      queryClient.invalidateQueries({ queryKey: QK.settings });
      showToast('Settings saved', 'success');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ color: '#475569', padding: '20px 0' }}>Loading…</div>;

  const disabled = !alertsEnabled;
  const freqMeta = FREQUENCIES.find((o) => o.value === frequency);

  return (
    <div>
      <div style={s.sectionLabel}>Email notifications</div>
      {disabled && (
        <div style={s.upgradeNote}>
          Email alerts are not available on your current plan. Upgrade to Starter or higher to enable notifications.
        </div>
      )}

      <div style={s.formGroup}>
        <label style={s.formLabel}>Frequency</label>
        <select style={s.select} value={frequency} disabled={disabled} onChange={(e) => setFrequency(e.target.value)}>
          {FREQUENCIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {freqMeta && <div style={{ ...s.rowSub, marginTop: 6 }}>{freqMeta.description}</div>}
      </div>

      {frequency !== 'off' && (
        <>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Send notifications to</label>
            <input
              type="email"
              style={s.input}
              disabled={disabled}
              value={notifEmail}
              placeholder={accountEmail || 'you@company.com'}
              onChange={(e) => setNotifEmail(e.target.value)}
            />
            <div style={{ ...s.rowSub, marginTop: 6 }}>
              Leave blank to use your account email{accountEmail ? ` (${accountEmail})` : ''}.
            </div>
          </div>

          <div style={{ ...s.sectionLabel, marginTop: 22 }}>Only notify me about tenders matching</div>

          <div style={s.formGroup}>
            <label style={s.formLabel}>Minimum match score <span style={hint}>(0 = any)</span></label>
            <div style={{ display: 'flex', gap: 8, maxWidth: 240 }}>
              <input
                type="number" min={0} max={100} value={minScore} disabled={disabled}
                style={{ ...s.input, textAlign: 'center' }}
                onChange={(e) => setMinScore(clampScore(e.target.value))}
              />
              <button type="button" style={stepBtn(disabled || minScore <= 0)}
                disabled={disabled || minScore <= 0} onClick={() => setMinScore((v) => clampScore(v - 1))}>−</button>
              <button type="button" style={stepBtn(disabled || minScore >= 100)}
                disabled={disabled || minScore >= 100} onClick={() => setMinScore((v) => clampScore(v + 1))}>+</button>
            </div>
          </div>

          {profiles.length > 0 && (
            <div style={s.formGroup}>
              <label style={s.formLabel}>Product profiles <span style={hint}>(empty = all)</span></label>
              <div style={checkBox}>
                {profiles.map((p) => (
                  <label key={p.id} style={chk}>
                    <input type="checkbox" disabled={disabled} checked={profileIds.includes(p.id)}
                      onChange={() => toggle(profileIds, p.id, setProfileIds)} /> {p.profile_name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={s.formGroup}>
            <label style={s.formLabel}>Countries <span style={hint}>(empty = all)</span></label>
            <div style={checkBox}>
              {COUNTRIES.map(([code, label]) => (
                <label key={code} style={chk}>
                  <input type="checkbox" disabled={disabled} checked={countries.includes(code)}
                    onChange={() => toggle(countries, code, setCountries)} /> {label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      <button style={s.saveBtn(saving || disabled)} onClick={handleSave} disabled={saving || disabled}>
        {saving ? 'Saving…' : 'Save settings'}
      </button>
    </div>
  );
}

// ── Tab: API Keys ─────────────────────────────────────────────────────────────

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://tendermatch-l1pr.onrender.com';

function ApiKeysTab() {
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const showToast = useToast();
  const queryClient = useQueryClient();
  const keysQuery = useApiKeys();
  const keys = keysQuery.data ?? [];
  const key = keys[0] || null;   // single-key model — an account holds at most one key
  const loading = keysQuery.isLoading;
  const forbidden = !!keysQuery.error
    && (keysQuery.error.message.includes('403') || keysQuery.error.message.includes('Professional'));

  async function handleGenerate() {
    if (key && !window.confirm('Regenerate your API key? The current key will stop working immediately.')) {
      return;
    }
    setCreating(true);
    try {
      const res = await apiFetch('/api-keys', {
        method: 'POST',
        body: JSON.stringify({ name: newKeyName.trim() || null }),
      });
      setRevealedKey(res.key);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: QK.apiKeys });
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id) {
    if (!window.confirm('Revoke your API key? Any integration using it will stop working.')) return;
    try {
      await apiFetch(`/api-keys/${id}`, { method: 'DELETE' });
      queryClient.invalidateQueries({ queryKey: QK.apiKeys });
      showToast('Key revoked', 'info');
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  if (loading) return <div style={{ color: '#475569', padding: '20px 0' }}>Loading…</div>;

  if (forbidden) {
    return <div style={s.upgradeNote}>API key access requires the Professional plan or higher.</div>;
  }

  return (
    <div>
      {revealedKey && (
        <div style={s.keyBox}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
            Your new API key — copy it now, it won't be shown again:
          </div>
          {revealedKey}
          <button
            style={{ ...s.smallBtn, marginLeft: 10, fontSize: 11 }}
            onClick={() => { navigator.clipboard.writeText(revealedKey); showToast('Copied!', 'success'); }}
          >Copy</button>
          <button style={{ ...s.smallBtn, marginLeft: 8, fontSize: 11 }} onClick={() => setRevealedKey(null)}>Dismiss</button>
        </div>
      )}

      {key ? (
        <>
          <div style={s.sectionLabel}>Your API key</div>
          <div style={s.row}>
            <div>
              <div style={s.rowLabel}>
                <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{key.key_prefix}…</span>
                {key.name && <span style={{ marginLeft: 10, fontSize: 13, color: '#94a3b8' }}>{key.name}</span>}
              </div>
              <div style={s.rowSub}>
                Created {new Date(key.created_at).toLocaleDateString()}
                {key.last_used_at && ` · Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.primarySmallBtn} onClick={handleGenerate} disabled={creating}>
                {creating ? 'Working…' : 'Regenerate'}
              </button>
              <button style={s.dangerBtn} onClick={() => handleRevoke(key.id)}>Revoke</button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={s.sectionLabel}>Generate API key</div>
          <div style={s.inputRow}>
            <input
              style={{ ...s.input, flex: 1 }}
              placeholder="Key name (optional)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
            <button style={s.primarySmallBtn} onClick={handleGenerate} disabled={creating}>
              {creating ? 'Creating…' : 'Generate'}
            </button>
          </div>
          <div style={s.emptyState}>No API key yet. Generate one to access your matches programmatically.</div>
        </>
      )}

      <div style={{ marginTop: 20, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
        Send requests with the header <code style={cd}>X-Api-Key: tm_…</code> instead of a Bearer token.
        The key is <strong>read-only</strong> and limited to your match data — <code style={cd}>GET /matches</code> and <code style={cd}>GET /matches/by-profile</code>. Rate limited to 60 requests/minute.
      </div>

      <button
        style={{ ...s.smallBtn, marginTop: 16 }}
        onClick={() => setShowGuide((v) => !v)}
      >
        {showGuide ? 'Hide integration guide' : 'Show integration guide'}
      </button>

      {showGuide && <ApiGuide />}
    </div>
  );
}

// Inline code style used across the API Keys tab.
const cd = { fontFamily: 'monospace', color: '#475569' };

function ApiGuide() {
  const showToast = useToast();
  const codeBlock = {
    position: 'relative',
    fontFamily: 'monospace',
    fontSize: 12.5,
    lineHeight: 1.6,
    color: '#e2e8f0',
    background: '#0f172a',
    border: '1px solid #1e293b',
    borderRadius: 8,
    padding: '14px 16px',
    margin: '8px 0 4px',
    whiteSpace: 'pre',
    overflowX: 'auto',
  };
  const h = { fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '22px 0 6px' };
  const p = { fontSize: 13, color: '#475569', lineHeight: 1.65, margin: '6px 0' };
  const cellH = { textAlign: 'left', padding: '6px 10px', fontSize: 12, fontWeight: 600, color: '#334155', borderBottom: '1px solid #e2e8f0' };
  const cell = { padding: '6px 10px', fontSize: 12.5, color: '#475569', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

  function Code({ children }) {
    return (
      <div style={codeBlock}>
        <button
          style={{ position: 'absolute', top: 8, right: 8, fontSize: 11, padding: '2px 8px', background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 5, cursor: 'pointer' }}
          onClick={() => { navigator.clipboard.writeText(children); showToast('Copied!', 'success'); }}
        >Copy</button>
        {children}
      </div>
    );
  }

  const curlExample = `curl -H "X-Api-Key: tm_your_key_here" \\
  "${API_BASE_URL}/matches"`;

  const pythonExample = `import requests

resp = requests.get(
    "${API_BASE_URL}/matches",
    headers={"X-Api-Key": "tm_your_key_here"},
    params={"profile_id": "optional-profile-uuid"},  # omit for all profiles
)
resp.raise_for_status()
matches = resp.json()

for m in matches:
    t = m["tender"]
    print(t["title"], "—", m["score"], m["verdict"])`;

  const jsExample = `const res = await fetch("${API_BASE_URL}/matches", {
  headers: { "X-Api-Key": "tm_your_key_here" },
});
const matches = await res.json();
console.log(matches);`;

  const responseExample = `[
  {
    "tender": {
      "id": "f1c2…",
      "ted_id": "2026-01234567",
      "title": "Supply of IT equipment",
      "description_en": "Framework agreement for …",
      "country": "DE",
      "cpv_codes": ["30200000", "30213000"],
      "deadline": "2026-08-01T23:59:00",
      "contract_value": 250000.0,
      "contract_size_band": "medium"
    },
    "score": 84.2,
    "reasoning": "Strong overlap on IT hardware supply.",
    "verdict": "great",
    "profile_id": "9b3e…",
    "source": "claude"
  }
]`;

  return (
    <div style={{ marginTop: 18, padding: '18px 20px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Integration guide</div>
      <p style={p}>
        The Jyto API lets you pull your tender matches into your own systems
        (spreadsheets, CRM, internal tools). It is <strong>read-only</strong> and scoped
        to your account's match data.
      </p>

      <div style={h}>1. Base URL</div>
      <Code>{API_BASE_URL}</Code>

      <div style={h}>2. Authentication</div>
      <p style={p}>
        Send your key in the <code style={cd}>X-Api-Key</code> header on every request.
        Keep it secret — anyone with the key can read your matches. If it leaks,
        regenerate it above (the old key stops working immediately).
      </p>
      <Code>X-Api-Key: tm_your_key_here</Code>

      <div style={h}>3. Endpoints</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', margin: '6px 0' }}>
        <thead>
          <tr>
            <th style={cellH}>Method &amp; path</th>
            <th style={cellH}>Returns</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cell}><code style={cd}>GET /matches</code><br />
              <span style={{ color: '#94a3b8' }}>?profile_id=&lt;uuid&gt; (optional)</span></td>
            <td style={cell}>A flat list of your matches. Without <code style={cd}>profile_id</code> it returns matches across all your profiles.</td>
          </tr>
          <tr>
            <td style={cell}><code style={cd}>GET /matches/by-profile</code></td>
            <td style={cell}>The same matches grouped into an object keyed by <code style={cd}>profile_id</code>.</td>
          </tr>
        </tbody>
      </table>

      <div style={h}>4. Examples</div>
      <p style={{ ...p, fontWeight: 600, marginBottom: 0 }}>cURL</p>
      <Code>{curlExample}</Code>
      <p style={{ ...p, fontWeight: 600, marginBottom: 0 }}>Python</p>
      <Code>{pythonExample}</Code>
      <p style={{ ...p, fontWeight: 600, marginBottom: 0 }}>JavaScript</p>
      <Code>{jsExample}</Code>

      <div style={h}>5. Response shape</div>
      <p style={p}>Each match is an object with a nested <code style={cd}>tender</code> and a few scoring fields:</p>
      <Code>{responseExample}</Code>

      <div style={h}>6. Field reference</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', margin: '6px 0' }}>
        <thead>
          <tr><th style={cellH}>Field</th><th style={cellH}>Type</th><th style={cellH}>Description</th></tr>
        </thead>
        <tbody>
          {[
            ['tender.id', 'string', 'Jyto tender id'],
            ['tender.ted_id', 'string', 'Official TED notice id'],
            ['tender.title', 'string', 'Tender title (English)'],
            ['tender.description_en', 'string', 'English description'],
            ['tender.country', 'string', '2-letter country code'],
            ['tender.cpv_codes', 'string[]', 'CPV procurement codes'],
            ['tender.deadline', 'datetime', 'Submission deadline'],
            ['tender.contract_value', 'number', 'Contract value (if published)'],
            ['tender.contract_size_band', 'string', 'small / medium / large'],
            ['score', 'number', 'Match relevance, 0–100'],
            ['verdict', 'string | null', 'great / good / maybe (null if not yet AI-scored)'],
            ['reasoning', 'string', 'One-line explanation (empty if not yet AI-scored)'],
            ['profile_id', 'string', 'Which of your profiles matched'],
            ['source', 'string', '"claude" (AI-scored) or "cosine" (similarity only)'],
          ].map(([f, t, d]) => (
            <tr key={f}>
              <td style={cell}><code style={cd}>{f}</code></td>
              <td style={cell}>{t}</td>
              <td style={cell}>{d}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={h}>7. Limits &amp; errors</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', margin: '6px 0' }}>
        <thead>
          <tr><th style={cellH}>Status</th><th style={cellH}>Meaning</th></tr>
        </thead>
        <tbody>
          {[
            ['200', 'Success'],
            ['401', 'Missing, invalid, or revoked key — or your plan no longer includes API access'],
            ['429', 'Rate limit exceeded (max 60 requests per minute per key)'],
          ].map(([code, meaning]) => (
            <tr key={code}>
              <td style={cell}><code style={cd}>{code}</code></td>
              <td style={cell}>{meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ ...p, marginTop: 10 }}>
        Match data refreshes as new tenders are ingested — polling once every few minutes
        is plenty. The API is read-only: saving, dismissing, and account changes are only
        available in the web app.
      </p>
    </div>
  );
}

// ── Tab: Integrations (CRM) ───────────────────────────────────────────────────

const chk = { fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' };

const tileGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
  gap: 16,
};
const tileCard = (hover) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9,
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  transform: hover ? 'scale(1.06)' : 'scale(1)',
  transition: 'transform 0.15s ease',
});
const tileSquare = (hover) => ({
  position: 'relative', width: '100%', aspectRatio: '1 / 1',
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: hover ? '0 6px 18px rgba(15,23,42,0.12)' : '0 1px 2px rgba(15,23,42,0.04)',
  transition: 'box-shadow 0.15s ease',
});
const tileName = { fontSize: 13, fontWeight: 500, color: '#334155', textAlign: 'center', lineHeight: 1.2 };

// TEMP: placeholder CRMs so we can see the layout with many providers.
// Non-functional (clicking shows a toast). Remove once real connectors exist.
const PLACEHOLDER_PROVIDERS = [
  { key: 'hubspot',    label: 'HubSpot',            color: '#FF7A59', placeholder: true },
  { key: 'salesforce', label: 'Salesforce',         color: '#00A1E0', placeholder: true },
  { key: 'zoho',       label: 'Zoho CRM',           color: '#E42527', placeholder: true },
  { key: 'dynamics',   label: 'Microsoft Dynamics', color: '#002050', placeholder: true },
  { key: 'freshsales', label: 'Freshsales',         color: '#FF5A3C', placeholder: true },
  { key: 'close',      label: 'Close',              color: '#4F46E5', placeholder: true },
  { key: 'copper',     label: 'Copper',             color: '#FF6F61', placeholder: true },
  { key: 'insightly',  label: 'Insightly',          color: '#FBA919', placeholder: true },
  { key: 'monday',     label: 'monday CRM',         color: '#FF3D57', placeholder: true },
  { key: 'sugarcrm',   label: 'SugarCRM',           color: '#E61718', placeholder: true },
];

const soonTag = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
  color: '#94a3b8', background: '#f1f5f9', borderRadius: 5, padding: '2px 6px',
};
const soonBadge = { position: 'absolute', top: 7, right: 7, ...soonTag };

// Provider logos — inline SVG so there's no runtime CDN dependency.
function ProviderLogo({ provider, size = 18, color }) {
  if (provider === 'pipedrive') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" style={{ flexShrink: 0 }}>
        <rect width="100" height="100" rx="24" fill="#017737" />
        <g transform="translate(24,2) scale(0.69)" fill="#fff">
          <path d="M44.138 23.65c-10.288 0-16.238 4.616-19.108 7.805-.341-2.748-2.154-6.313-9.227-6.313H.443V41.22h6.295c1.065 0 1.408.34 1.408 1.403v73.412h18.31V88.482c0-.744-.015-1.439-.034-2.061 2.86 2.627 8.322 6.243 16.854 6.243 17.896 0 30.408-14.186 30.408-34.504 0-20.64-11.88-34.51-29.546-34.51m-3.72 53.066c-9.857 0-14.335-9.438-14.335-18.182 0-13.773 7.532-18.682 14.58-18.682 8.643 0 14.456 7.452 14.456 18.553 0 12.659-7.39 18.311-14.7 18.311" />
        </g>
      </svg>
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: Math.round(size * 0.22), background: color || '#475569', color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.5), fontWeight: 700, flexShrink: 0,
    }}>
      {(provider || '?').charAt(0).toUpperCase()}
    </span>
  );
}

function ConnectionCard({ conn, profiles, onChanged }) {
  const showToast = useToast();
  const [open, setOpen] = useState(false);
  const rules = conn.routing_rules || {};
  const f0 = rules.filters || {};
  const [minScore, setMinScore] = useState(f0.min_score ?? 0);
  const [verdicts, setVerdicts] = useState(f0.verdicts || []);
  const [profileIds, setProfileIds] = useState(f0.profile_ids || []);
  const [labelId, setLabelId] = useState((rules.default_target?.label_ids || [])[0] || '');
  const [labels, setLabels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);

  // Lazy-load the CRM's lead labels the first time the editor opens.
  useEffect(() => {
    if (open && labels === null) {
      apiFetch(`/integrations/connections/${conn.id}/routing-options`)
        .then((groups) => {
          const g = (groups || []).find((x) => x.field === 'label_ids');
          setLabels(g ? g.options : []);
        })
        .catch(() => setLabels([]));
    }
  }, [open]); // eslint-disable-line

  const toggleIn = (arr, val, setter) =>
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  async function handleSave() {
    setSaving(true);
    const filters = {};
    if (Number(minScore) > 0) filters.min_score = Number(minScore);
    if (verdicts.length) filters.verdicts = verdicts;
    if (profileIds.length) filters.profile_ids = profileIds;
    const routing_rules = { filters };
    if (labelId) routing_rules.default_target = { label_ids: [labelId] };
    try {
      await apiFetch(`/integrations/connections/${conn.id}`, {
        method: 'PATCH', body: JSON.stringify({ routing_rules }),
      });
      showToast('Rules saved', 'success');
      onChanged();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function patch(updates) {
    setBusy(true);
    try {
      await apiFetch(`/integrations/connections/${conn.id}`, { method: 'PATCH', body: JSON.stringify(updates) });
      onChanged();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setBusy(false); }
  }

  async function handleTest() {
    setBusy(true);
    try {
      const r = await apiFetch(`/integrations/connections/${conn.id}/test`, { method: 'POST' });
      showToast(r.ok ? 'Connection OK' : `Failed: ${r.detail}`, r.ok ? 'success' : 'error');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!window.confirm('Remove this connection? Tenders will stop syncing to it.')) return;
    setBusy(true);
    try {
      await apiFetch(`/integrations/connections/${conn.id}`, { method: 'DELETE' });
      onChanged();
    } catch (e) { showToast(e.message, 'error'); setBusy(false); }
  }

  async function toggleLog() {
    if (log !== null) { setLog(null); return; }
    try { setLog(await apiFetch(`/integrations/connections/${conn.id}/log`) || []); }
    catch (e) { showToast(e.message, 'error'); }
  }

  return (
    <div style={{ ...s.row, display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={s.rowLabel}>{conn.name}</span>
          <span style={s.badge('#60a5fa')}>{conn.provider}</span>
          {!conn.is_active && <span style={s.badge('#94a3b8')}>paused</span>}
        </div>
        <button style={s.smallBtn} onClick={() => setOpen((v) => !v)}>{open ? 'Close' : 'Edit rules'}</button>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={s.sectionLabel}>When to send a tender</div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Minimum fit score</label>
            <select style={s.select} value={minScore} onChange={(e) => setMinScore(Number(e.target.value))}>
              <option value={0}>Any score</option>
              <option value={50}>50+</option>
              <option value={70}>70+</option>
              <option value={85}>85+</option>
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Verdict (leave empty for any)</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {['great', 'good', 'maybe'].map((v) => (
                <label key={v} style={chk}>
                  <input type="checkbox" checked={verdicts.includes(v)} onChange={() => toggleIn(verdicts, v, setVerdicts)} /> {v}
                </label>
              ))}
            </div>
          </div>
          {profiles.length > 0 && (
            <div style={s.formGroup}>
              <label style={s.formLabel}>Product profiles (leave empty for all)</label>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {profiles.map((p) => (
                  <label key={p.id} style={chk}>
                    <input type="checkbox" checked={profileIds.includes(p.id)} onChange={() => toggleIn(profileIds, p.id, setProfileIds)} /> {p.profile_name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ ...s.sectionLabel, marginTop: 16 }}>Where it lands</div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Apply lead label (optional)</label>
            <select style={s.select} value={labelId} onChange={(e) => setLabelId(e.target.value)}>
              <option value="">No label</option>
              {(labels || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            {labels === null && <div style={s.rowSub}>Loading labels…</div>}
          </div>

          <button style={s.saveBtn(saving)} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save rules'}
          </button>

          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            <button style={s.smallBtn} onClick={handleTest} disabled={busy}>Test connection</button>
            <button style={s.smallBtn} onClick={() => patch({ is_active: !conn.is_active })} disabled={busy}>
              {conn.is_active ? 'Pause' : 'Resume'}
            </button>
            <button style={s.smallBtn} onClick={toggleLog}>{log === null ? 'View activity' : 'Hide activity'}</button>
            <button style={s.dangerBtn} onClick={handleDelete} disabled={busy}>Remove</button>
          </div>

          {log !== null && (
            <div style={{ marginTop: 12 }}>
              {log.length === 0 ? (
                <div style={s.emptyState}>No tenders sent yet.</div>
              ) : log.map((r, i) => (
                <div key={i} style={{ ...s.rowSub, display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                  <span style={{ color: r.status === 'sent' ? '#16a34a' : '#dc2626', textAlign: 'right' }}>
                    {r.status}{r.error ? ` · ${r.error.slice(0, 60)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IntegrationsTab() {
  const [connecting, setConnecting] = useState('');
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState('');
  const showToast = useToast();
  const queryClient = useQueryClient();
  const providersQuery = useIntegrationProviders();
  const connectionsQuery = useIntegrationConnections();
  const profilesQuery = useProfiles();
  const providers = providersQuery.data ?? [];
  const connections = connectionsQuery.data ?? [];
  const profiles = (profilesQuery.data ?? []).map((p) => ({ id: p.id, profile_name: p.profile_name }));
  const loading = providersQuery.isLoading || connectionsQuery.isLoading;
  const err = providersQuery.error || connectionsQuery.error;
  const forbidden = !!err && (err.message.includes('403') || err.message.toLowerCase().includes('professional'));
  // Refresh the connections list after connect/patch/delete.
  const load = () => queryClient.invalidateQueries({ queryKey: QK.integrationConnections });

  async function handleConnect(p) {
    if (p.placeholder) { showToast(`${p.label} integration coming soon`, 'info'); return; }
    const provider = p.key;
    setConnecting(provider);
    try {
      const { session_token } = await apiFetch('/integrations/connect-session', {
        method: 'POST', body: JSON.stringify({ provider }),
      });
      const nango = new Nango();
      const connect = nango.openConnectUI({
        onEvent: async (event) => {
          if (event.type === 'connect') {
            try {
              await apiFetch('/integrations/connections', {
                method: 'POST',
                body: JSON.stringify({ provider, nango_connection_id: event.payload.connectionId }),
              });
              showToast('Connected!', 'success');
              load();
            } catch (e) { showToast(e.message, 'error'); }
            finally { setConnecting(''); }
          } else if (event.type === 'close') {
            setConnecting('');
          }
        },
      });
      connect.setSessionToken(session_token);
    } catch (e) {
      showToast(e.message, 'error');
      setConnecting('');
    }
  }

  if (loading) return <div style={{ color: '#475569', padding: '20px 0' }}>Loading…</div>;
  if (forbidden) return <div style={s.upgradeNote}>CRM integrations require the Professional plan or higher.</div>;

  const allProviders = [...providers, ...PLACEHOLDER_PROVIDERS];
  const q = query.trim().toLowerCase();
  const shownProviders = q
    ? allProviders.filter((p) => p.label.toLowerCase().includes(q) || p.key.toLowerCase().includes(q))
    : allProviders;
  const shownConnections = q
    ? connections.filter((c) => (c.name || '').toLowerCase().includes(q) || c.provider.toLowerCase().includes(q))
    : connections;

  return (
    <div>
      <input
        style={{ ...s.input, marginBottom: 20 }}
        placeholder="Search CRMs…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div style={s.sectionLabel}>Connect a CRM</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, lineHeight: 1.5 }}>
        Send matching tenders straight into your sales tool as leads — your team triages which to pursue.
      </div>
      <div style={tileGrid}>
        {shownProviders.map((p) => {
          const hover = hovered === p.key;
          return (
            <button
              key={p.key}
              style={tileCard(hover)}
              onClick={() => handleConnect(p)}
              onMouseEnter={() => setHovered(p.key)}
              onMouseLeave={() => setHovered('')}
              disabled={!!connecting}
            >
              <div style={{ ...tileSquare(hover), opacity: p.placeholder ? 0.78 : 1 }}>
                <ProviderLogo provider={p.key} color={p.color} size={52} />
                {p.placeholder && <span style={soonBadge}>Soon</span>}
              </div>
              <span style={tileName}>{connecting === p.key ? 'Connecting…' : p.label}</span>
            </button>
          );
        })}
      </div>
      {shownProviders.length === 0 && (
        <div style={s.emptyState}>No CRMs match “{query}”.</div>
      )}

      {connections.length > 0 ? (
        <>
          <div style={{ ...s.sectionLabel, marginTop: 24 }}>Connected</div>
          {shownConnections.map((c) => (
            <ConnectionCard key={c.id} conn={c} profiles={profiles} onChanged={load} />
          ))}
          {shownConnections.length === 0 && (
            <div style={s.emptyState}>No connections match “{query}”.</div>
          )}
        </>
      ) : (
        <div style={{ ...s.emptyState, marginTop: 20 }}>No CRM connected yet.</div>
      )}
    </div>
  );
}

// ── Tab: Usage ────────────────────────────────────────────────────────────────

function UsageTab() {
  const usageQuery = useUsage();
  const usage = usageQuery.data ?? null;

  if (usageQuery.isLoading) return <div style={{ color: '#475569', padding: '20px 0' }}>Loading…</div>;
  if (!usage) return null;

  const stats = [
    { label: 'Active profiles', value: usage.profiles_active, max: usage.profiles_max },
    { label: 'Matches (total)', value: usage.matches_total, max: null },
  ];

  return (
    <div>
      <div style={s.sectionLabel}>This month</div>
      <div style={s.statGrid}>
        {stats.map((st) => (
          <div key={st.label} style={s.statBox}>
            <div style={s.statLabel}>{st.label}</div>
            <div style={s.statValue}>{st.value ?? 0}</div>
            {st.max != null && (
              <div style={s.statMax}>of {st.max} on your plan</div>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 13, color: '#475569' }}>
        Plan: <strong style={{ color: '#94a3b8' }}>{usage.plan || '—'}</strong>
        {usage.trial_days_remaining != null && usage.trial_days_remaining > 0 && (
          <span style={{ marginLeft: 12, color: '#fbbf24' }}>
            Trial — {usage.trial_days_remaining} day{usage.trial_days_remaining !== 1 ? 's' : ''} remaining
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main Settings page ────────────────────────────────────────────────────────

const TABS = [
  { id: 'notifications', label: 'Email Notifications' },
  { id: 'api-keys',      label: 'API Keys' },
  { id: 'integrations',  label: 'Integrations' },
  { id: 'usage',         label: 'Usage' },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState('notifications');

  return (
    <div style={s.page}>
      <div style={s.heading}>Settings</div>
      <div style={s.sub}>Manage your account preferences and integrations.</div>

      <div style={s.tabs}>
        {TABS.map((tab) => (
          <button key={tab.id} style={s.tab(activeTab === tab.id)} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'notifications' && <EmailNotificationsTab />}
      {activeTab === 'api-keys'      && <ApiKeysTab />}
      {activeTab === 'integrations'  && <IntegrationsTab />}
      {activeTab === 'usage'         && <UsageTab />}
    </div>
  );
}
